import Events from 'editor/components/Events';
import { MapboxEventArgs } from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import MapSheetEvents from 'editor/components/map/MapboxEvents/MapSheetEvents';
import MapSheet from 'editor/components/map/MapSheet';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import PageID from 'editor/components/PageID';
import { getSheetLayerHits } from 'editor/utils/map';

const clickMarkupEvents = {
  click(args: MapboxEventArgs<mapboxgl.MapEventType['click']>): void {
    const { world, evt } = args;

    const sheetLayerHits = getSheetLayerHits(
      world,
      evt.lngLat.lng,
      evt.lngLat.lat
    );

    const noSheetsClicked = sheetLayerHits.every((lh) => lh.hit === null);

    // If no sheets were clicked, then that means the map was clicked.
    // In this case, deselect all markups, and call sheet deselect events,
    // and short circuit the rest of the function.
    if (noSheetsClicked) {
      world.grabAll(Markup).forEach(({ component: markupComp }) => {
        markupComp.selected = false;
      });

      world.locateAll([MapSheet, Events]).forEach((ent) => {
        const events = ent.get<Events<MapSheetEvents>>(Events);
        events.events.emit('markups:deselect-all');
      });

      return;
    }

    // keeping a tally of all selected markups across sheets so we know
    // if "markups:deselect-all" events will need to fire.
    const selectedMarkups = new Set();

    sheetLayerHits.forEach((lh): void => {
      const { entity: sheetEntity, hit } = lh;
      const { pageid } = sheetEntity.get(PageID);
      const events = sheetEntity.get<Events<MapSheetEvents>>(Events);
      const markupEntites = world.getAllTagged(`markup-on-${pageid}`);

      markupEntites.forEach((markupEntity): void => {
        const markupComp = markupEntity.get(Markup);
        const { MKPoint } = markupEntity.get(MarkupKit);

        if (!hit) {
          markupComp.selected = false;
          return;
        }

        const l = hit.clone();
        const point = new MKPoint(l.x, l.y);
        const isHit = markupComp.mkMarkup?.hit(point);

        if (isHit && !markupComp.hidden) {
          events.events.emit('markup:click', {
            markup: markupComp.markup,
          });

          selectedMarkups.add(markupComp.markup.markupid);
        }
      });
    });

    // If a Sheet was clicked, but no markup was clicked, then call deselect events on sheets.
    if (selectedMarkups.size === 0) {
      sheetLayerHits.forEach(({ entity }) => {
        const events = entity.get<Events<MapSheetEvents>>(Events);

        events.events.emit('markups:deselect-all');
      });
    }
  },
};

export default clickMarkupEvents;
